import { Box, Typography } from '@mui/material';
import { motion, AnimatePresence } from 'framer-motion';
import { Arrow, BLOCK_WIDTH, Caption, Cursor, FacebookAd, type CursorState } from './DemoShared';
import { PRIMARY, EASE_OUT_QUINT } from './howItWorksConstants';

const DAY_LABELS = ['L', 'M', 'M', 'J', 'V', 'S', 'D'];
const DAY_CELLS = [
  { num: 8, label: 'Lun 8', weekend: false },
  { num: 9, label: 'Mar 9', weekend: false },
  { num: 10, label: 'Mer 10', weekend: false },
  { num: 11, label: 'Jeu 11', weekend: false },
  { num: 12, label: 'Ven 12', weekend: false },
  { num: 13, label: 'Sam 13', weekend: true },
  { num: 14, label: 'Dim 14', weekend: true },
];
const TIME_SLOTS = ['10:00', '11:00', '14:00', '15:00', '16:00', '17:00'];

const BookingCard = ({
  selectedDate,
  selectedSlot,
  showSlots,
  cursor,
  cursorMode,
}: {
  selectedDate: number | null;
  selectedSlot: string | null;
  showSlots: boolean;
  cursor: CursorState;
  cursorMode: 'date' | 'slot';
}) => (
  <Box
    sx={{
      bgcolor: '#fff',
      borderRadius: 2,
      border: '1px solid #e5e7eb',
      boxShadow: '0 4px 14px rgba(0,0,0,0.06)',
      p: 2,
      width: BLOCK_WIDTH,
      position: 'relative',
    }}
  >
    <Typography sx={{ fontSize: 14, fontWeight: 700, color: '#1a1a1a', mb: 0.5 }}>
      Réserver une séance
    </Typography>
    <Typography sx={{ fontSize: 11, color: '#64748b', mb: 2 }}>Choisissez un créneau</Typography>

    {/* Day headers */}
    <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)', mb: 0.5, gap: 0.3 }}>
      {DAY_LABELS.map((l, i) => (
        <Box
          key={`dh-${i}`}
          sx={{ textAlign: 'center', fontSize: 10, fontWeight: 600, color: '#94a3b8' }}
        >
          {l}
        </Box>
      ))}
    </Box>

    {/* Day cells */}
    <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)', gap: 0.3, mb: 1.5 }}>
      {DAY_CELLS.map((d) => {
        const isSelected = selectedDate === d.num;
        return (
          <Box
            key={d.num}
            sx={{
              width: 32,
              height: 32,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              borderRadius: 1,
              fontSize: 12,
              fontWeight: isSelected ? 700 : 500,
              bgcolor: isSelected ? PRIMARY : 'transparent',
              color: isSelected ? '#fff' : d.weekend ? '#cbd5e1' : '#1a1a1a',
              transition: 'background-color 200ms, color 200ms',
              cursor: 'default',
            }}
          >
            {d.num}
          </Box>
        );
      })}
    </Box>

    {/* Time slots */}
    <AnimatePresence>
      {showSlots && (
        <motion.div
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: -6 }}
          transition={{ duration: 0.4, ease: EASE_OUT_QUINT as [number, number, number, number] }}
        >
          <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: 0.7 }}>
            {TIME_SLOTS.map((slot) => {
              const isSelected = selectedSlot === slot;
              return (
                <Box
                  key={slot}
                  sx={{
                    border: '1px solid',
                    borderColor: isSelected ? PRIMARY : '#e5e7eb',
                    borderRadius: 1,
                    py: 0.8,
                    textAlign: 'center',
                    fontSize: 12,
                    fontWeight: 600,
                    bgcolor: isSelected ? PRIMARY : '#fff',
                    color: isSelected ? '#fff' : '#1a1a1a',
                    transition: 'background-color 200ms, color 200ms, border-color 200ms',
                    cursor: 'default',
                  }}
                >
                  {slot}
                </Box>
              );
            })}
          </Box>
        </motion.div>
      )}
    </AnimatePresence>

    <Cursor
      visible={cursor.visible}
      clicking={cursor.clicking}
      bottom={cursorMode === 'slot' ? 70 : 35}
      right={cursorMode === 'slot' ? 195 : 130}
    />
  </Box>
);

const RdvCard = ({ highlight }: { highlight: boolean }) => (
  <Box
    sx={{
      bgcolor: '#fff',
      borderRadius: 2,
      border: '1px solid #e5e7eb',
      boxShadow: '0 4px 14px rgba(0,0,0,0.06)',
      overflow: 'hidden',
      width: BLOCK_WIDTH,
    }}
  >
    <Box
      sx={{
        bgcolor: '#f8fafc',
        borderBottom: '1px solid #e5e7eb',
        px: 1.8,
        py: 1.2,
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
      }}
    >
      <Typography sx={{ fontSize: 13, fontWeight: 700, color: '#1a1a1a' }}>Mes RDV</Typography>
    </Box>
    <Box sx={{ p: 1.5, minHeight: 100 }}>
      <AnimatePresence mode="wait">
        {highlight ? (
          <motion.div
            key="rdv-card"
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -8 }}
            transition={{
              duration: 0.55,
              ease: EASE_OUT_QUINT as [number, number, number, number],
            }}
          >
            <Box
              component={motion.div}
              animate={{
                boxShadow: [
                  '0 0 0 0px rgba(59,130,246,0)',
                  '0 0 0 4px rgba(59,130,246,0.28)',
                  '0 0 0 2px rgba(59,130,246,0.14)',
                ],
              }}
              transition={{ duration: 1.6, repeat: Infinity, repeatType: 'loop' }}
              sx={{
                border: '1px solid rgba(59,130,246,0.4)',
                borderRadius: 2,
                p: 1.5,
                bgcolor: '#fff',
              }}
            >
              <Typography sx={{ fontSize: 12, fontWeight: 700, color: PRIMARY, mb: 0.3 }}>
                Jeudi 11 décembre · 14:00
              </Typography>
              <Typography sx={{ fontSize: 13, fontWeight: 700, color: '#0a2540', mb: 0.2 }}>
                Marie Bernard
              </Typography>
              <Typography
                sx={{ fontSize: 12, color: '#475569', fontVariantNumeric: 'tabular-nums' }}
              >
                06 12 34 56 78
              </Typography>
            </Box>
          </motion.div>
        ) : (
          <motion.div
            key="rdv-empty"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
          >
            <Box sx={{ py: 4, textAlign: 'center', color: '#94a3b8', fontSize: 12 }}>
              En attente…
            </Box>
          </motion.div>
        )}
      </AnimatePresence>
    </Box>
  </Box>
);

// Affichage direct du résultat final (3 étapes), sans animation de lecture.
const Demo2 = () => (
  <Box
    sx={{
      display: 'flex',
      flexDirection: { xs: 'column', md: 'row' },
      alignItems: 'center',
      justifyContent: 'center',
      gap: { xs: 2, md: 1.5 },
      flexWrap: 'nowrap',
      overflowX: { xs: 'visible', md: 'auto' },
    }}
  >
    {/* Block 1 — FB Ad */}
    <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
      <FacebookAd
        cursor={{ visible: false, clicking: false }}
        heroTitle="Réservez votre séance"
        ctaLabel="Prendre RDV"
      />
      <Caption number={1}>Le client voit une pub facebook</Caption>
    </Box>

    <Arrow />

    {/* Block 2 — Booking card */}
    <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
      <BookingCard
        selectedDate={11}
        selectedSlot="14:00"
        showSlots
        cursor={{ visible: false, clicking: false }}
        cursorMode="slot"
      />
      <Caption number={2}>Le client prend RDV</Caption>
    </Box>

    <Arrow />

    {/* Block 3 — RDV card */}
    <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
      <RdvCard highlight />
      <Caption number={3}>Vous recevez le rendez-vous, sans rien faire</Caption>
    </Box>
  </Box>
);

export default Demo2;
